import {
  MemberBulkButtonComponents,
  MemberTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { BulkChangeProjectRole } from "@components/table/members/bulk-actions/bulk-change-project-role";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMembersProps } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { useAppSelector } from "@store/store-helper";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { isTeam } from "@utils/team-utils";
import { DASH } from "@utils/ui-utils";
import { isMemberActive } from "@utils/user-utils";
import { useMemo } from "react";

interface Props extends Partial<BaseCompanyIdProps>, BaseMembersProps {
  /** The selected project if there is one */
  project?: SdbProject | null;
}

/** List all the bulk action components for project team table */
export function ProjectTeamBulkActions({
  companyId,
  members,
  project,
}: Props): MemberBulkButtonComponents {
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);
  const filteredMembers = members.filter((member) =>
    selectedRowIds.some((identity) => identity === member.identity)
  );

  const isAnyRowSelected = filteredMembers.length !== 0;
  const isAnyMemberPending = filteredMembers.some(
    (member) => !isMemberActive(member)
  );
  const isAnyMemberTeam = filteredMembers.some((member) => isTeam(member));

  const { canChangeRole, canDeleteMemberFromProject } =
    useHasUserValidPermissionProjectLevel({
      project,
    });

  const allowedButtonsBasedOnRowSelection: MemberTableBulkName[] =
    useMemo(() => {
      const allowedButtons: MemberTableBulkName[] = ["export"];

      if (project && isAnyRowSelected) {
        if (!isAnyMemberTeam) {
          allowedButtons.push("sendEmail");
        }

        if (canChangeRole) {
          allowedButtons.push("changeRole");
        }

        if (isAnyMemberPending) {
          allowedButtons.push("resendInvitation");
        }

        if (canDeleteMemberFromProject) {
          allowedButtons.push("removeMember");
        }
      }

      return allowedButtons;
    }, [
      canChangeRole,
      canDeleteMemberFromProject,
      isAnyMemberPending,
      isAnyMemberTeam,
      isAnyRowSelected,
      project,
    ]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Determine whether to show the export button */
  const shouldShowExportButton =
    shouldShowBulkButtons("export") &&
    (isFetchingProjects || members.length > 0);

  if (!companyId || !project) {
    return {
      changeRole: null,
      resendInvitation: null,
      removeMember: null,
      sendEmail: null,
      export: null,
    };
  }

  return {
    changeRole: shouldShowBulkButtons("changeRole") ? (
      <BulkChangeProjectRole companyId={companyId} projectId={project.id} />
    ) : null,

    resendInvitation: shouldShowBulkButtons("resendInvitation") ? (
      <BulkResendInvitation
        selectedEntities={filteredMembers}
        companyId={companyId}
        projectId={project.id}
        subjectType="project"
      />
    ) : null,

    removeMember: shouldShowBulkButtons("removeMember") ? (
      <BulkRemoveMember
        subjectType="project"
        companyId={companyId}
        projectId={project.id}
      />
    ) : null,

    sendEmail: shouldShowBulkButtons("sendEmail") ? (
      <BulkSendEmail selectedEntities={filteredMembers} />
    ) : null,

    export: shouldShowExportButton ? (
      <BulkExportMembers
        selectedEntities={isAnyRowSelected ? filteredMembers : members}
        subjectType="project"
        entityName={project.name ?? DASH}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,
  };
}
